/**
 * Blocks the UI while an accounting fix, revert, or scan is running.
 * Prevents accidental double-clicks and warns if the user tries to leave the page.
 */
import { useEffect, useState } from 'react';

const PHASE_MESSAGES = {
  apply: [
    'Validating preview…',
    'Updating ledger rows…',
    'Recalculating costs if needed…',
    'Saving audit log…',
    'Verifying books…',
  ],
  wac_apply: [
    'Loading fuel tanks and purchases…',
    'Replaying weighted-average cost through the period…',
    'Updating purchase cost on nozzle closings…',
    'Updating recoup sales costs…',
    'Saving all ledger changes…',
  ],
  revert: [
    'Loading undo snapshot…',
    'Restoring previous values…',
    'Saving undo log…',
  ],
  preview: [
    'Counting shop sales in date range…',
    'Checking GST ledger balance…',
    'Preparing repair summary…',
  ],
  scan: [
    'Checking balance sheet vs profit…',
    'Reviewing fuel costs…',
    'Scanning stock and cash…',
  ],
};

export default function AccountingFixProcessingOverlay({
  visible,
  mode = 'apply',
  title,
  message,
  safetyNote,
  livePhase = null,
  progressDetail = null,
}) {
  const [phaseIndex, setPhaseIndex] = useState(0);
  const phases = PHASE_MESSAGES[mode] || PHASE_MESSAGES.apply;

  useEffect(() => {
    if (!visible || livePhase) {
      setPhaseIndex(0);
      return undefined;
    }

    const timer = setInterval(() => {
      setPhaseIndex((current) => (current + 1) % phases.length);
    }, 2800);

    return () => clearInterval(timer);
  }, [visible, mode, phases.length, livePhase]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="accounting-fix-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="accounting-fix-overlay-backdrop" />
      <div className="accounting-fix-overlay-card">
        <div className="accounting-fix-overlay-spinner" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <h5 className="accounting-fix-overlay-title">{title}</h5>
        <p className="accounting-fix-overlay-message mb-2">{message}</p>
        <p className="accounting-fix-overlay-phase">{livePhase || phases[phaseIndex]}</p>
        {progressDetail && (
          <p className="accounting-fix-overlay-progress-detail small text-muted mb-2">{progressDetail}</p>
        )}
        {safetyNote && (
          <div className="accounting-fix-overlay-safety">
            <strong>Safe fix:</strong> {safetyNote}
          </div>
        )}
        <p className="accounting-fix-overlay-hint small mb-0">
          Do not close this tab, refresh, or click other buttons until this finishes.
        </p>
      </div>
    </div>
  );
}

export function useAccountingProcessingGuard(active, warningMessage) {
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = warningMessage;
      return warningMessage;
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [active, warningMessage]);
}
